import { useEffect, useRef, useState } from "react";
import api from "../api";
import { CardIntro, DateInput, Field, Segmented } from "./ui/Form";

/**
 * Spray notification emails (ROADMAP B1, Kathy).
 *
 * Driven by what is already scheduled. The treatment areas carry their own
 * subgrids, date and time, so the recipient list follows from the schedule
 * and Kathy types nothing -- an earlier version asked her for subgrid labels
 * and a free-text date, which is both more work and another place to get a
 * week wrong.
 *
 * Sending is per row. Each resident has their own button, so Kathy decides
 * household by household and can stop at any point; a "send the rest" button
 * exists because a large week runs to hundreds and clicking each is not a
 * real option, but it is the secondary path rather than the default.
 *
 * Anyone already told about this same scheduled treatment is marked and
 * their button is spent. The mark is keyed on the run, not the address, so
 * the same household is notified again the next time their area is sprayed.
 */

interface Recipient {
  email: string;
  name: string;
  address: string;
  type: string;
  zone: string;
  /** Which map the notice belongs to, from where the resident actually is. */
  trs?: string;
  city?: string;
  sent?: boolean;
}

interface Area {
  object_id: number;
  gridlabels: string[];
  treatment_date: string | null;
  treatment_time: string | null;
  disease: string | null;
}

/** A resident as the layer holds them, whatever they asked for. */
interface Contact {
  object_id: number;
  name: string;
  address: string;
  email: string;
  /** Verbatim. Seven digits is normal here, and some records hold two
   *  numbers separated by a slash, so nothing parses or validates it. */
  phone: string;
  type: string;
  zone: string;
  trs?: string;
  city?: string;
  do_not_spray: boolean;
  sent?: boolean;
}

interface Contacts {
  email: Contact[];
  text: Contact[];
  phone: Contact[];
  none: Contact[];
}

/** One row of `summarise` -- what the subgrids hold, by stated preference. */
interface Group {
  type: string;
  rows: number;
  with_email: number;
  with_phone: number;
  do_not_spray: number;
}

interface Scheduled {
  areas: Area[];
  subgrids: string[];
  recipients: Recipient[];
  contacts: Contacts;
  /** Asked to be emailed, but the Email field holds nothing deliverable. */
  no_address?: Contact[];
  /** Caught by the buffer but in no other list. Should usually be empty. */
  left_out?: (Contact & { reason: string })[];
  resident_count?: number;
  groups?: Group[];
  run_key: string;
  treatment_dates: string[];
  treatment_times: string[];
  manual?: boolean;
  /** What the City and TRS lookup could not do, in a sentence. */
  place_note?: string;
}

/** One recorded action: an email sent, or a text or call ticked off. */
interface LogEntry {
  day: string;
  time: string;
  action: string;
  name: string;
  address: string;
  zone: string;
  type: string;
  email?: string;
  phone?: string;
  treatment_date: string;
  trs?: string;
  city?: string;
  sent_by: string;
  ok: boolean;
  error?: string;
}

interface Job {
  state: string;
  total: number;
  sent: number;
  failed: number;
}

const DEFAULT_SUBJECT = "Adult mosquito control treatment in your area";

/** The District's notice, as Kathy wrote it. {date} and {time} come from the
 *  scheduled treatment area; the rest is fixed wording. It is deliberately
 *  addressed to "Resident" rather than by name -- that is how the District
 *  words it, and a mail-merged first name on a public health notice invites
 *  the question of where the name came from. */
const DEFAULT_BODY = `<p>Dear Resident,</p>

<p>You are receiving this notice because you have requested to be informed of
adult mosquito control treatments in your neighborhood.</p>

<p>The Consolidated Mosquito Abatement District has identified an elevated
risk of mosquito-borne illness in your area. To help reduce this risk, the
District will conduct targeted ultra-low volume (ULV) applications using
registered insecticides to control adult mosquitoes.</p>

<p><strong>Treatment Schedule (weather permitting):</strong><br/>
Date: {date}<br/>
Time: {time} (During this timeframe)</p>

<p>You can view currently scheduled treatment areas on the District's website:<br/>
<a href="https://www.consolidatedmadca.gov/scheduled-treatment-areas-for-adult-mosquitoes">https://www.consolidatedmadca.gov/scheduled-treatment-areas-for-adult-mosquitoes</a></p>

<p>You do not need to leave the area during spraying. However, if you prefer,
you may choose to remain indoors and close windows and doors. This precaution
is optional and not required.</p>

<p>The application does not pose a risk to pets, but you may choose to bring
them inside during the treatment as an added precaution. If you experience any
health concerns following the application, please contact your healthcare
provider.</p>

<p>For additional information about truck-mounted mosquito control
applications, please visit:<br/>
<a href="https://www.cdc.gov/mosquitoes/mosquito-control/truck-spraying.html">https://www.cdc.gov/mosquitoes/mosquito-control/truck-spraying.html</a></p>

<p>We also encourage you to protect yourself and your family from mosquito
bites by using insect repellent when outdoors:<br/>
<a href="https://www.cdc.gov/mosquitoes/prevention/">https://www.cdc.gov/mosquitoes/prevention/</a></p>

<p>If you have any questions, please contact the District office.</p>

<p>Sincerely,<br/>
Consolidated Mosquito Abatement District<br/>
13151 E Industrial Dr, Parlier, CA 93648<br/>
559.896.1085</p>`;

/** AGOL hands dates back as epoch milliseconds. */
function readableDate(value: string | null) {
  if (!value) return "";
  const n = Number(value);
  // A typed date is YYYY-MM-DD. `new Date("2026-10-15")` reads that as midnight
  // UTC, which is still the 14th in California, so the label said the day
  // before the one that was typed. Built from its parts it is local midnight.
  const typed = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  const d = typed
    ? new Date(Number(typed[1]), Number(typed[2]) - 1, Number(typed[3]))
    : new Date(isNaN(n) ? value : n);
  return isNaN(d.getTime())
    ? String(value)
    : d.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
}

export default function SprayNotifyCard() {
  const [data, setData] = useState<Scheduled | null>(null);
  const [rows, setRows] = useState<Recipient[]>([]);
  /** What was actually done, on any day, read back from the delivery log.
   *  The log has always recorded every send and every tick; nothing could
   *  ask it for a particular day, so the week was only visible while the
   *  page stayed open. */
  const [history, setHistory] = useState<LogEntry[] | null>(null);
  const [historyDays, setHistoryDays] = useState<string[]>([]);
  const [historyDay, setHistoryDay] = useState("");
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyNote, setHistoryNote] = useState("");

  const loadHistory = async (day?: string) => {
    setHistoryBusy(true);
    try {
      const q = day ? `?day=${day}` : "";
      const r = await api.get(`/spray-notify-log${q}`);
      setHistory(r.data.entries || []);
      setHistoryDays(r.data.days || []);
      setHistoryNote(r.data.place_note || "");
      // The server says which day it resolved "latest" to.
      if (r.data.day) setHistoryDay(r.data.day);
      else if (!day && (r.data.days || []).length) setHistoryDay(r.data.days[0]);
    } catch {
      setHistory([]);
    } finally {
      setHistoryBusy(false);
    }
  };

  const downloadHistory = () => {
    // Served by the backend rather than built here: it is the same log, and
    // two writers of the same report drift.
    const q = historyDay ? `?day=${historyDay}&format=csv` : "?format=csv";
    window.open(`${api.defaults.baseURL ?? ""}/spray-notify-log${q}`, "_blank");
  };

  // Read on arrival. The history answers "what did we do", which does not
  // depend on what is scheduled now, so it should not wait for a button.
  useEffect(() => {
    loadHistory("latest");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const EMPTY: Contacts = { email: [], text: [], phone: [], none: [] };
  const [contacts, setContacts] = useState<Contacts>(EMPTY);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [testTo, setTestTo] = useState("");
  // A blind copy of each notice, so the sender keeps a record of what went
  // out. Blind rather than cc: each notice carries one resident, and a
  // visible copy would show them who else is on the list.
  const [bcc, setBcc] = useState("");
  const [testSent, setTestSent] = useState(false);
  const [sending, setSending] = useState<Record<string, boolean>>({});
  const [failed, setFailed] = useState<Record<string, string>>({});
  const [job, setJob] = useState<Job | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  // Notices sometimes go out with no scheduled treatment behind them, so the
  // list can also be built from subgrids typed by hand -- the same ones the
  // CSV tool takes. The date and time then have to be typed too, since there
  // is no treatment area to read them from.
  const [source, setSource] = useState<"scheduled" | "manual">("scheduled");
  const [typed, setTyped] = useState("");
  // Pickers rather than free text. The notice prints these verbatim, so a
  // typo reaches residents -- and "Sep 30, 2026" is not the format the
  // District's notice uses, which is the sort of thing nobody checks twice.
  const [manualDate, setManualDate] = useState("");
  const [startTime, setStartTime] = useState("20:30");
  const [endTime, setEndTime] = useState("00:00");
  // Where a resident's reply lands. The sender is a shared mailbox nobody
  // watches, and a reply to a spray notice is usually worth reading -- that
  // they have moved, or keep bees. Remembered per browser so it is typed
  // once rather than every week.
  const [replyTo, setReplyTo] = useState(
    () => window.localStorage.getItem("pioneer_spray_reply_to") || ""
  );
  // Default to whoever is signed in. Kathy is not always the one sending --
  // if she is away and Jodi runs the week, replies should reach Jodi without
  // her having to notice an empty field whose empty state loses them.
  useEffect(() => {
    api
      .get("/session-status")
      .then((r) => {
        const mine = r.data?.user?.email;
        if (!mine) return;
        setReplyTo(mine);
        setTestTo((v) => v || mine);
        setBcc((v) => v || mine);
      })
      .catch(() => {});
    // Only ever fills a blank field, so it cannot overwrite a deliberate choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    try {
      window.localStorage.setItem("pioneer_spray_reply_to", replyTo);
    } catch {
      /* private windows refuse storage; typing it again is no hardship */
    }
  }, [replyTo]);
  const poll = useRef<number | null>(null);

  const load = async () => {
    setBusy("load");
    setError(null);
    try {
      // Two routes, not one with a flag: the manual one never looks at the
      // treatment layer, and that should be obvious from the call.
      let url = "/spray-notify-scheduled";
      if (source === "manual") {
        if (!typed.trim()) {
          setBusy("");
          setError("Type at least one subgrid.");
          return;
        }
        const q = new URLSearchParams({ subgrids: typed.trim() });
        if (manualDate) q.set("date", manualDate);
        if (timeRange) q.set("time", timeRange);
        url = `/spray-notify-list?${q}`;
      }
      const r = await api.get(url);
      setData(r.data);
      setRows(r.data.recipients || []);
      setContacts(r.data.contacts || EMPTY);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          (source === "manual"
            ? "Could not build the list for those subgrids."
            : "Could not read the schedule.")
      );
    } finally {
      setBusy("");
    }
  };

  // Deliberately no auto-load. Reading the schedule on mount meant opening
  // the card to type subgrids still fired a schedule query, and any schedule
  // problem surfaced while you were plainly not using the schedule. It also
  // ran a buffered AGOL query against every scheduled area on page open,
  // whether or not anyone wanted it. The button starts it, in both modes.
  useEffect(() => {
    return () => {
      if (poll.current) window.clearInterval(poll.current);
    };
  }, []);

  // The notice prints date and time on separate lines, so they are separate
  // placeholders rather than one joined string.
  // "8:30 PM - 12:00 AM", the way the notice words it.
  const clock = (hhmm: string) => {
    const [h, m] = (hhmm || "").split(":").map(Number);
    if (isNaN(h)) return "";
    const ampm = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")} ${ampm}`;
  };
  const timeRange = [clock(startTime), clock(endTime)].filter(Boolean).join(" - ");
  const when = data?.manual
    ? readableDate(manualDate)
    : data?.treatment_dates?.length
    ? readableDate(data.treatment_dates[0])
    : "";
  const time = (data?.manual ? timeRange : data?.treatment_times?.[0]) || "";
  // Residents who asked for text but hold no number anyone can dial. They
  // are dropped from the text list by the backend, so without this they
  // disappear from the week entirely.
  const textGroup = data?.groups?.find((g) => g.type.toLowerCase() === "text");
  const noNumber = textGroup ? textGroup.rows - textGroup.with_phone : 0;
  const context = {
    date: when,
    time,
    when: [when, time].filter(Boolean).join(", "),
    zones: (data?.subgrids || []).join(", "),
  };

  const sendOne = async (r: Recipient) => {
    setSending((p) => ({ ...p, [r.email]: true }));
    setFailed((p) => {
      const n = { ...p };
      delete n[r.email];
      return n;
    });
    try {
      await api.post("/spray-notify-one", {
        recipient: r,
        subject,
        body,
        context,
        reply_to: replyTo.trim(),
        bcc: bcc.trim(),
        run_key: data?.run_key,
      });
      setRows((p) => p.map((x) => (x.email === r.email ? { ...x, sent: true } : x)));
    } catch (e: any) {
      setFailed((p) => ({ ...p, [r.email]: e?.response?.data?.message || "Failed" }));
    } finally {
      setSending((p) => ({ ...p, [r.email]: false }));
    }
  };

  /** Pioneer sends no texts and places no calls. Kathy works through these
   *  from her phone; this records that she did, so a week spread over several
   *  days stays trackable. Keyed on the record rather than the number,
   *  because a seven-digit number is normal here and two households can
   *  share one. */
  const markContacted = async (person: Contact, channel: "text" | "phone") => {
    const key = String(person.object_id);
    setSending((p) => ({ ...p, [key]: true }));
    setFailed((p) => ({ ...p, [key]: "" }));
    try {
      await api.post("/spray-notify-mark-texted", {
        recipient: person,
        run_key: data?.run_key,
        channel,
      });
      setContacts((prev) => ({
        ...prev,
        [channel]: prev[channel].map((x) =>
          x.object_id === person.object_id ? { ...x, sent: true } : x
        ),
      }));
    } catch (e: any) {
      setFailed((p) => ({ ...p, [key]: e?.response?.data?.message || "Failed" }));
    } finally {
      setSending((p) => ({ ...p, [key]: false }));
    }
  };

  const sendTest = async () => {
    setBusy("test");
    setError(null);
    try {
      await api.post("/spray-notify-test", {
        to: testTo.trim(),
        recipient: rows[0],
        subject,
        body,
        context,
        reply_to: replyTo.trim(),
        bcc: bcc.trim(),
      });
      setTestSent(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || "The test could not be sent.");
    } finally {
      setBusy("");
    }
  };

  const sendRest = async () => {
    setBusy("rest");
    setError(null);
    try {
      const r = await api.post("/spray-notify-remaining", {
        recipients: rows.filter((x) => !x.sent),
        subject,
        body,
        context,
        reply_to: replyTo.trim(),
        bcc: bcc.trim(),
        run_key: data?.run_key,
      });
      poll.current = window.setInterval(async () => {
        try {
          const s = await api.get(`/spray-notify-status/${r.data.job_id}`);
          setJob(s.data);
          if (s.data.state === "done") {
            window.clearInterval(poll.current!);
            poll.current = null;
            load();
          }
        } catch {
          /* retried next tick */
        }
      }, 2000);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Could not start.");
    } finally {
      setBusy("");
    }
  };

  const pending = rows.filter((r) => !r.sent).length;
  const done = rows.length - pending;

  return (
    <div className="space-y-4">
      <CardIntro>
        The residents inside this week's treatment areas, from the schedule or
        from subgrids you type. Send one to yourself first — these reach the
        public and cannot be recalled.
      </CardIntro>

      {error && <div className="p-3 rounded-sm2 bg-wnv-tint text-wnv text-[13px]">{error}</div>}

      <div className="ui-card p-4">
        <Field label="Where does this week's list come from?">
          <Segmented
            value={source}
            onChange={(v) => {
              setSource(v);
              setData(null);
              setRows([]);
              setTestSent(false);
            }}
            options={[
              ["scheduled", "Scheduled areas"],
              ["manual", "Type subgrids"],
            ]}
          />
        </Field>
        {source === "manual" && (
          <div className="mt-3 grid gap-3 min-[720px]:grid-cols-3">
            <Field label="Subgrids" hint="Comma or space separated.">
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="132118, 132119"
                className="w-full px-3 py-2 border border-line-strong rounded-sm2 text-[13px]"
              />
            </Field>
            <Field label="Treatment date" hint="Fills {date}.">
              <DateInput
                value={manualDate}
                onChange={(e) => setManualDate(e.target.value)}
              />
            </Field>
            <Field
              label="Time"
              hint={timeRange ? `Reads as "${timeRange}".` : "Fills {time}."}
            >
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="flex-1 min-w-0 px-2 py-2 border border-line-strong rounded-sm2 text-[13px]"
                />
                <span className="text-ink-faint text-[13px]">to</span>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="flex-1 min-w-0 px-2 py-2 border border-line-strong rounded-sm2 text-[13px]"
                />
              </div>
            </Field>
          </div>
        )}
        <button
          onClick={load}
          disabled={!!busy || (source === "manual" && !typed.trim())}
          className="btn-primary text-sm mt-3"
        >
          {busy === "load" ? "Reading…" : source === "manual" ? "Build the list" : "Read the schedule"}
        </button>
      </div>

      {data && !data.manual && data.areas.length === 0 && (
        <div className="ui-card p-4 text-[13px] text-ink-soft">
          Nothing is scheduled. Areas appear here once they are published as{" "}
          <strong>Upcoming</strong> in the Treatment Area Builder — or switch to{" "}
          <strong>Type subgrids</strong> above to send without one.
        </div>
      )}

      {data && (data.manual ? data.subgrids.length > 0 : data.areas.length > 0) && (
        <>
          <div className="ui-card p-4">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="font-extrabold text-[15px]">
                {data.manual
                  ? `${data.subgrids.length} subgrid${data.subgrids.length === 1 ? "" : "s"}, typed`
                  : `${data.areas.length} area${data.areas.length === 1 ? "" : "s"} scheduled`}
              </h3>
              {when && <span className="pill bg-brand-tint text-brand-deep">{when}</span>}
              <button onClick={load} className="text-xs text-ink-faint hover:underline ml-auto">
                Refresh
              </button>
            </div>
            <p className="text-ink-soft text-[13px] mt-1">
              Subgrids {data.subgrids.join(", ") || "—"} ·{" "}
              {data.resident_count ?? rows.length} resident
              {(data.resident_count ?? rows.length) === 1 ? "" : "s"} in the buffered
              area · {rows.length} to email, {contacts.text.length} to text,{" "}
              {contacts.phone.length} to call
              {done > 0 && ` · ${done} already emailed`}
            </p>
            {/* Everyone the subgrids contain, including the preferences
                Pioneer cannot act on. Phone and None are not sent anything
                by anyone, and a screen that lists only the sendable ones
                reads as though the week is covered. */}
            {!!data.groups?.length && (
              <p className="text-ink-faint text-[12px] mt-1">
                {data.groups
                  .map((g) => `${g.rows} asked for ${g.type.toLowerCase()}`)
                  .join(" · ")}
              </p>
            )}
            {/* Said on the page because the alternative is a column of dashes,
                which looks exactly like "nothing to find". */}
            {!!data.place_note && (
              <p className="text-[12px] text-wnv mt-1">{data.place_note}</p>
            )}
          </div>

          <div className="ui-card p-4">
            <div className="flex flex-wrap gap-2 items-center">
              <input
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="your.name@consolidatedmadca.gov"
                className="flex-1 min-w-[15rem] px-3 py-2 border border-line-strong rounded-sm2 text-[13px]"
              />
              <button
                onClick={sendTest}
                disabled={!testTo.trim() || !!busy}
                className="btn-primary text-sm !py-1.5"
              >
                {busy === "test" ? "Sending…" : "Send this week's test to me"}
              </button>
              <button
                onClick={() => setShowEditor((v) => !v)}
                className="btn-ghost text-sm !py-1.5"
              >
                {showEditor ? "Hide message" : "Edit message"}
              </button>
            </div>
            {testSent && (
              <p className="text-[13px] text-brand-deep mt-2 font-semibold">
                Test sent. Check it reads correctly before emailing anyone.
              </p>
            )}
            {showEditor && (
              <div className="mt-3 space-y-3">
                <Field
                  label="Replies go to"
                  hint="Defaults to whoever is signed in. Sent from notifications@consolidatedmadca.gov, which nobody monitors, so a blank here loses replies."
                >
                  <input
                    value={replyTo}
                    onChange={(e) => setReplyTo(e.target.value)}
                    placeholder="kathy@consolidatedmadca.gov"
                    className="w-full px-3 py-2 border border-line-strong rounded-sm2 text-[13px]"
                  />
                </Field>
                <Field
                  label="Blind copy each notice to"
                  hint="Keeps a copy of what went out. Blind, so residents never see it."
                >
                  <input
                    value={bcc}
                    onChange={(e) => setBcc(e.target.value)}
                    placeholder="leave blank for none"
                    className="w-full px-3 py-2 border border-line-strong rounded-sm2 text-[13px]"
                  />
                </Field>
                <Field label="Subject">
                  <input
                    value={subject}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      setTestSent(false);
                    }}
                    className="w-full px-3 py-2 border border-line-strong rounded-sm2 text-[13px]"
                  />
                </Field>
                <Field
                  label="Message"
                  hint="{date} and {time} come from the scheduled treatment area. {name}, {address} and {zone} are also available."
                >
                  <textarea
                    value={body}
                    onChange={(e) => {
                      setBody(e.target.value);
                      setTestSent(false);
                    }}
                    rows={12}
                    className="w-full px-3 py-2 border border-line-strong rounded-sm2 text-[12px] font-mono"
                  />
                </Field>
              </div>
            )}
          </div>

          {rows.length > 0 && (
            <div className="ui-card p-0 overflow-hidden">
              <div className="flex flex-wrap items-center gap-2 p-4 pb-3">
                <h3 className="font-extrabold text-[15px]">
                  {pending} to send{done > 0 && `, ${done} done`}
                </h3>
                <button
                  onClick={sendRest}
                  disabled={!testSent || !pending || !!busy || !!job}
                  className="btn-ghost text-sm !py-1.5 ml-auto"
                  title={!testSent ? "Send yourself a test first" : ""}
                >
                  {busy === "rest" ? "Starting…" : `Send the remaining ${pending}`}
                </button>
              </div>
              {!testSent && (
                <p className="text-[12px] text-ink-faint px-4 pb-3">
                  Send yourself a test before emailing residents.
                </p>
              )}
              {job && (
                <div className="px-4 pb-3">
                  <div className="h-1.5 rounded-full bg-paper-2 overflow-hidden">
                    <div
                      className="h-full bg-brand rounded-full transition-all"
                      style={{
                        width: `${job.total ? ((job.sent + job.failed) / job.total) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <div className="text-[12px] text-ink-faint mt-1">
                    {job.state === "done" ? "Finished" : "Sending"} {job.sent} of {job.total}
                    {job.failed > 0 && ` · ${job.failed} failed`}
                  </div>
                </div>
              )}
              <div className="overflow-auto max-h-[28rem] border-t border-line">
                <table className="w-full text-[13px]">
                  <thead className="bg-paper-2 sticky top-0">
                    <tr>
                      {["Name", "Address", "City", "TRS", "Email", "Asked for", ""].map((h) => (
                        <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.email} className="border-t border-line">
                        <td className="px-3 py-2">{r.name || "—"}</td>
                        <td className="px-3 py-2 text-ink-soft max-w-[18rem] truncate">
                          {r.address || "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.city || "—"}</td>
                        <td className="px-3 py-2 font-mono text-[12px] whitespace-nowrap">{r.trs || "—"}</td>
                        <td className="px-3 py-2 font-mono text-[12px]">{r.email}</td>
                        <td className="px-3 py-2 text-ink-faint">{r.type}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {r.sent ? (
                            <span className="pill bg-brand-tint text-brand-deep">Sent</span>
                          ) : (
                            <>
                              <button
                                onClick={() => sendOne(r)}
                                disabled={!testSent || sending[r.email] || !!job}
                                className="btn-ghost text-xs !py-1"
                              >
                                {sending[r.email] ? "…" : "Email"}
                              </button>
                              {failed[r.email] && (
                                <div className="text-[11px] text-wnv mt-0.5">
                                  {failed[r.email]}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!!data.no_address?.length && (
            <div className="ui-card p-0 overflow-hidden">
              <div className="flex flex-wrap items-center gap-2 p-4 pb-3">
                <h3 className="font-extrabold text-[15px]">
                  {data.no_address.length} asked for email but have no usable address
                </h3>
                <span className="pill bg-sle-tint text-sle">By hand</span>
              </div>
              <p className="text-[12px] text-ink-faint px-4 pb-3">
                The Email field for these residents is empty or is not an address, so
                nothing above reaches them. Fix the record in the layer, or contact them
                another way.
              </p>
              <div className="overflow-auto max-h-[20rem] border-t border-line">
                <table className="w-full text-[13px]">
                  <thead className="bg-paper-2 sticky top-0">
                    <tr>
                      {["Name", "Address", "City", "TRS", "Email field says", "Phone"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.no_address.map((p) => (
                      <tr key={p.object_id ?? p.name} className="border-t border-line">
                        <td className="px-3 py-2">{p.name || "—"}</td>
                        <td className="px-3 py-2 text-ink-soft">{p.address || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{p.city || "—"}</td>
                        <td className="px-3 py-2 font-mono text-[12px] whitespace-nowrap">{p.trs || "—"}</td>
                        <td className="px-3 py-2 font-mono text-[12px]">{p.email || "(blank)"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{p.phone || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!!data.left_out?.length && (
            <div className="ui-card p-0 overflow-hidden">
              <h3 className="font-extrabold text-[15px] p-4 pb-2">
                {data.left_out.length} need a look
              </h3>
              <div className="overflow-auto max-h-[20rem] border-t border-line">
                <table className="w-full text-[13px]">
                  <tbody>
                    {data.left_out.map((p) => (
                      <tr key={p.object_id ?? p.name} className="border-t border-line">
                        <td className="px-3 py-2">{p.name || "—"}</td>
                        <td className="px-3 py-2 text-ink-soft">{p.address || "—"}</td>
                        <td className="px-3 py-2 font-mono text-[12px]">{p.email || p.phone || "—"}</td>
                        <td className="px-3 py-2 text-[12px] text-ink-soft">{p.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* One pane per channel reached by hand. Both are shown even when
              empty: a missing pane made "nobody here asked for this" look
              identical to "the list is broken", which is what it was. */}
          {(["text", "phone"] as const).map((channel) => {
            const people = contacts[channel];
            const outstanding = people.filter((p) => !p.sent).length;
            const label = channel === "text" ? "text" : "call";
            return (
              <div key={channel} className="ui-card p-0 overflow-hidden">
                <div className="flex flex-wrap items-center gap-2 p-4 pb-3">
                  <h3 className="font-extrabold text-[15px]">
                    {outstanding} to {label}
                    {people.length > outstanding &&
                      `, ${people.length - outstanding} done`}
                  </h3>
                  <span className="pill bg-sle-tint text-sle">By hand</span>
                </div>
                <p className="text-[12px] text-ink-faint px-4 pb-3">
                  {people.length > 0 ? (
                    <>
                      These residents asked to be {channel === "text" ? "texted" : "called"},
                      so the emails above do not reach them. Pioneer cannot{" "}
                      {channel === "text" ? "send texts" : "place calls"} — work through
                      them and tick each one off, so a week spread over several days
                      stays trackable.
                    </>
                  ) : (
                    <>Nobody inside these subgrids asked to be {channel === "text" ? "texted" : "called"}.</>
                  )}
                </p>
                {people.length > 0 && (
                  <div className="overflow-auto max-h-[28rem] border-t border-line">
                    <table className="w-full text-[13px]">
                      <thead className="bg-paper-2 sticky top-0">
                        <tr>
                          {["Name", "Address", "City", "TRS", "Number", "Asked for", ""].map((h) => (
                            <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {people.map((p) => (
                          <tr key={p.object_id} className="border-t border-line">
                            <td className="px-3 py-2">{p.name || "—"}</td>
                            <td className="px-3 py-2 text-ink-soft max-w-[18rem] truncate">
                              {p.address || "—"}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{p.city || "—"}</td>
                            <td className="px-3 py-2 font-mono text-[12px] whitespace-nowrap">{p.trs || "—"}</td>
                            {/* Shown exactly as recorded: no reformatting, no
                                validation. Seven digits means the 559 area
                                code, and a slash means two numbers. */}
                            <td className="px-3 py-2 font-mono text-[12px]">
                              {p.phone || "—"}
                            </td>
                            <td className="px-3 py-2 text-ink-faint">{p.type}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {p.sent ? (
                                <span className="pill bg-brand-tint text-brand-deep">
                                  {channel === "text" ? "Texted" : "Called"}
                                </span>
                              ) : (
                                <>
                                  <button
                                    onClick={() => markContacted(p, channel)}
                                    disabled={sending[String(p.object_id)]}
                                    className="btn-ghost text-xs !py-1"
                                  >
                                    {sending[String(p.object_id)]
                                      ? "…"
                                      : channel === "text"
                                        ? "Mark as texted"
                                        : "Mark as called"}
                                  </button>
                                  {failed[String(p.object_id)] && (
                                    <div className="text-[11px] text-wnv mt-0.5">
                                      {failed[String(p.object_id)]}
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {contacts.none.length > 0 && (
            <div className="ui-card p-4">
              <h3 className="font-extrabold text-[15px]">
                {contacts.none.length} asked for nothing
              </h3>
              <p className="text-[12px] text-ink-faint mt-1">
                These records inside the buffered area state no contact preference,
                so none of the lists above includes them. Holding an address is not
                the same as asking to be written to — they are here so the count
                reconciles with the CSV export, not as people to contact.
              </p>
              <ul className="text-[12px] text-ink-soft mt-2 space-y-0.5">
                {contacts.none.map((p) => (
                  <li key={p.object_id ?? p.name}>
                    {p.name || "—"} · {p.address || "no address"} · {p.type}
                    {p.email ? ` · ${p.email}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

        </>
      )}

      {/* Not part of the schedule block above. What was done on Tuesday is
          the same fact whether or not anything is scheduled today, and once
          the week is marked complete the schedule is empty -- which used to
          take the record of what had been sent down with it. */}
      <div className="ui-card p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-extrabold text-[15px]">What has been done</h3>
          {historyDays.length > 0 && (
            <select
              value={historyDay}
              onChange={(e) => {
                setHistoryDay(e.target.value);
                loadHistory(e.target.value);
              }}
              className="text-xs px-2 py-1 border border-line-strong rounded-sm2 bg-card"
            >
              {historyDays.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={() => loadHistory(historyDay || undefined)}
            disabled={historyBusy}
            className="btn-ghost text-sm !py-1"
          >
            {historyBusy ? "Reading\u2026" : history ? "Refresh" : "Show"}
          </button>
          {!!history?.length && (
            <button onClick={downloadHistory} className="btn-ghost text-sm !py-1 ml-auto">
              Download CSV
            </button>
          )}
        </div>
        <p className="text-[12px] text-ink-faint mt-1">
          Every email sent and every text or call ticked off, with the time it
          happened. Kept on the server, so it survives logging out and is still
          here tomorrow.
        </p>

        {!!historyNote && (
          <p className="text-[12px] text-wnv mt-2">{historyNote}</p>
        )}

        {history && history.length === 0 && (
          <p className="text-[13px] text-ink-faint mt-2">
            Nothing recorded{historyDay ? ` on ${historyDay}` : " yet"}.
          </p>
        )}

        {!!history?.length && (
          <div className="overflow-auto max-h-[24rem] border border-line rounded-sm2 mt-3">
            <table className="w-full text-[12px]">
              <thead className="bg-paper-2 sticky top-0">
                <tr>
                  {["Time", "Action", "Name", "Address", "City", "TRS", "Contact", "By"].map((h) => (
                    <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((e, i) => (
                  <tr key={`${e.day}-${e.time}-${i}`} className="border-t border-line">
                    <td className="px-2 py-1 whitespace-nowrap text-ink-faint">{e.time}</td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      {e.ok ? (
                        e.action
                      ) : (
                        <span className="text-wnv" title={e.error}>
                          {e.action}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1">{e.name || "\u2014"}</td>
                    <td className="px-2 py-1 text-ink-soft max-w-[16rem] truncate">
                      {e.address || "\u2014"}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">{e.city || "\u2014"}</td>
                    <td className="px-2 py-1 font-mono text-[11px] whitespace-nowrap">{e.trs || "\u2014"}</td>
                    <td className="px-2 py-1 font-mono text-[11px]">
                      {e.email || e.phone || "\u2014"}
                    </td>
                    <td className="px-2 py-1 text-ink-faint">{e.sent_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
